import React, { useState, useEffect, useReducer, useRef, useMemo } from "react";
import {
  Tabs,
  Tab,
  Grid,
  Box,
  Card,
  IconButton,
  Input,
  InputAdornment,
  Typography,
  Stack,
} from "@mui/material";
import dayjs from "dayjs";
import AddIcon from "@mui/icons-material/Add";
import Button from "@mui/material/Button";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { get } from "aws-amplify/api";
import {
  DataGrid,
  GridToolbarContainer,
  GridActionsCellItem,
  GridToolbarColumnsButton,
  GridRowModes,
  useGridApiRef,
} from "@mui/x-data-grid";
import { I18n } from "@aws-amplify/core";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import currentUserToken from "CurrentUserToken";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SearchIcon from "@mui/icons-material/Search";
import { useTheme } from "@mui/material/styles";
import { post } from "aws-amplify/api";
import { APIStatusDialog } from "components/APIStatusDialog";
import EncodeDialog from "popup/EncodeDialog";
import TemplateBox from "components/TemplateBox";
import { useNavigate } from "react-router-dom"; // Import useNavigate for navigation
import { useLocation } from "react-router-dom";
import { DataGridPro } from "@mui/x-data-grid-pro/DataGridPro";
import { GridToolbarExport, GridToolbarFilterButton } from "@mui/x-data-grid";
import EncodingSettingsMenu from "../components/EncodingSettingsMenu";
import PatternSettingsMenu from "../components/PatternSettingsMenu";
import { itIT } from "@mui/x-data-grid/locales";
import { deDE } from "@mui/x-data-grid/locales";

import EventHistorySettingsMenu from "../components/EventHistorySettingsMenu";
const reducer = (lastState, action) => {
  if (action.type === "DATA_RECEIVED") {
    const audit = action.value;
    if (Array.isArray(audit)) {
      const uniqueAudit = audit.filter(
        (item, index, self) =>
          index === self.findIndex((el) => el.id === item.id)
      );

      uniqueAudit.forEach((element) => {
        if (typeof element.timestamp === "number") {
          element.timestamp = dayjs(element.timestamp).format(
            "YYYY-MM-DD HH:mm"
          );
        }
      });
      return { audit: uniqueAudit, fetched: true };
    }
  }
  return lastState;
};
function Billing() {
  const [state, dispatch] = useReducer(reducer, {
    audit: [],
    fetched: false,
  });
  const [openEncodeDialog, setOpenEncodeDialog] = useState(false);
  const [apiStatus, setApiStatus] = useState({
    open: false,
    status: "",
    message: "",
  });
  const [resizeThrottle, setResizeThrottle] = useState(60);
  const [languages, setLanguages] = useState([]);
  const [tableParentIsMD, setTableParentIsMD] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const navigate = useNavigate(); // Use navigate to switch between routes
  const location = useLocation();
  const toolbarRef = useRef(null);
  const handleEncodeDialogOpen = () => {
    setOpenEncodeDialog(true); // Open the Encode dialog
  };

  const handleEncodeDialogClose = () => {
    setOpenEncodeDialog(false); // Close the Encode dialog
  };

  useEffect(() => {
    const parentElement = toolbarRef.current;
    if (!parentElement) {
      console.error("toolbarRef is not correctly attached to any DOM element.");
      return;
    }
    const checkSize = (width) => {
      const breakpoint = 960; // 960px is md
      setTableParentIsMD(width < breakpoint);
    };
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        checkSize(entry.contentRect.width);
      }
    });
    resizeObserver.observe(parentElement);
    checkSize(parentElement.offsetWidth);
    return () => {
      resizeObserver.disconnect();
    };
  }, []);
  const apiRef = useGridApiRef();
  const [searchText, setSearchText] = useState("");
  const theme = useTheme();
  const [language, setLanguage] = useState(
    localStorage.getItem("language") || "en"
  );
  const [rowModesModel, setRowModesModel] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [filters, setFilters] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const handleRowSelection = (ids) => {
    setSelectedRows(ids);
  };
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    const hiddenColumns = JSON.parse(
      localStorage.getItem("hiddenColumns") || "[]"
    );
    if (apiRef.current) {
      apiRef.current.setColumnVisibilityModel(
        hiddenColumns.reduce((model, field) => {
          model[field] = false;
          return model;
        }, {})
      );
    }
  }, [apiRef]);

  const handleColumnVisibilityChange = (newModel) => {
    const hiddenColumns = Object.keys(newModel).filter(
      (field) => !newModel[field]
    );
    localStorage.setItem("hiddenColumns", JSON.stringify(hiddenColumns));
  };

  const tlmGetAlarmDescription = async () => {
    try {
      const restOperation = get({
        apiName: "TelemonitoringAPI",
        path: "/alarm/map/allUncoded",
        options: {
          headers: {
            "Content-Type": "application/json",
            Authorization: `${await currentUserToken()}`,
          },
        },
      });
      const { body } = await restOperation.response;
      const response = await body.json();

      dispatch({ type: "DATA_RECEIVED", value: response });
    } catch (error) {
      console.error("Errore durante la chiamata API:", error);
    }
  };
  useEffect(() => {
    tlmGetAlarmDescription();
  }, []);

  useEffect(() => {
    const tlmGetLanguagesAPI = async () => {
      try {
        const restOperation = get({
          apiName: "TelemonitoringAPI",
          path: "/languages",
          options: {
            headers: {
              "Content-Type": "application/json",
              Authorization: `${await currentUserToken()}`,
            },
            queryParams: {},
          },
        });
        const { body } = await restOperation.response;
        const response = await body.json();

        setLanguages(response);
      } catch (error) {
        console.error("Errore durante la chiamata API:", error);
      }
    };
    tlmGetLanguagesAPI();
  }, []);

  const columns = useMemo(
    () => [
      { field: "alarm_text", headerName: I18n.get("Alarm Text"), width: 700 },
      {
        field: "account_name",
        headerName: I18n.get("Account Name"),
        width: 400,
      },
      {
        field: "local_monitoring_system_type",
        headerName: I18n.get("Type"),
        width: 200,
      },
      {
        field: "local_monitoring_system_manufacturer",
        headerName: I18n.get("Manufacturer"),
        width: 200,
      },
      {
        field: "source_account",
        headerName: I18n.get("Source Account"),
        width: 200,
      },
    ],
    []
  );

  const applyStoreFiltering = (value) => {
    let matchesAllFilters = true;
    if (searchText) {
      const searchTextLower = searchText.toLowerCase();
      matchesAllFilters =
        matchesAllFilters &&
        Object.values(value).some((val) =>
          String(val).toLowerCase().includes(searchTextLower)
        );
    }
    return matchesAllFilters;
  };
  const filteredRows = state.audit.filter(applyStoreFiltering);
  const filteredRowsCount = filteredRows.length;
  const searchChanged = (event) => {
    setSearchText(event.target.value);
  };
  const renderToolbar = () => {
    return (
      <div ref={toolbarRef}>
        <Stack direction="row" alignItems="center" spacing={2} mb={1}>
          <Typography variant="h5" mt={0.5} mb={1}>
            {I18n.get("Encoded Alarms Text")} (
            {filters ? filteredRowsCount : totalCount})
          </Typography>
        </Stack>
        <Stack
          direction={tableParentIsMD ? "column" : "row"}
          alignItems="center"
          justifyContent={tableParentIsMD ? "center" : "space-between"}
          spacing={2}
          sx={{ pb: 2 }}
        >
          <Stack
            direction={tableParentIsMD ? "column" : "row"}
            spacing={2}
            sx={{ width: "100%" }}
          >
            <Button
              onClick={handleEncodeDialogOpen}
              color="primary"
              variant="outlined"
              startIcon={<AddIcon />}
              fullWidth={tableParentIsMD}
              disabled={selectedRows.length === 0} // Disabilita se nessuna riga è selezionata
            >
              {I18n.get("Encode")}
            </Button>
          </Stack>
          <Input
            type="text"
            placeholder={I18n.get("Search")}
            onChange={searchChanged}
            fullWidth={tableParentIsMD}
            endAdornment={
              <InputAdornment position="end">
                <IconButton>
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            }
          />
        </Stack>
      </div>
    );
  };
  function getBrowserName() {
    const userAgent = navigator.userAgent;
    if (userAgent.includes("Firefox")) {
      return "Firefox";
    } else if (userAgent.includes("Edg")) {
      return "Edge";
    } else if (userAgent.includes("Chrome")) {
      return "Chrome";
    }
  }
  const browser = getBrowserName();
  // useEffect(()=>{ --> controllo dei duplicati
  //   if(state.audit.length>0){
  //     const idCount={};
  //     const duplicates=[];
  //     state.audit.forEach((item)=>{
  //       if(idCount[item.id]){
  //         idCount[item.id]++;

  //       if(idCount[item.id]===2){
  //         duplicates.push(item.id)
  //       }
  //     }else{
  //       idCount[item.id]=1
  //     }
  //     });if(duplicates.length>0){
  //       console.log("id duplicati",duplicates)
  //     }
  //   }
  // }, [
  // ])
  useEffect(() => {
    document.body.style.overflow = "hidden";
  }, []);
  const [tableHeight, setTableHeight] = useState(0);
  useEffect(() => {
    const updateHeight = () => {
      const navbarHeight = document.getElementById("navbar");
      const footerHeight = document.getElementById("footer");
      const margin =
        browser === "Firefox" ? 200 : browser === "Chrome" ? 200 : 200;
      const availableHeight =
        document.documentElement.clientHeight -
        navbarHeight -
        footerHeight -
        margin;
      setTableHeight(availableHeight);
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);
  const darkMode = localStorage.getItem("darkMode") === "true";
  return (
    <>
      <DashboardLayout>
        <DashboardNavbar
          absolute
          light
          language={language}
          setLanguage={setLanguage}
        />
        <Stack
          direction="row"
          spacing={1}
          sx={{ ml: 3, mt: 3, width: "400px" }}
        >
          <EncodingSettingsMenu />
          <PatternSettingsMenu />
          <EventHistorySettingsMenu />
        </Stack>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ p: 2, maxHeight: `${tableHeight}px` }}>
              {" "}
              {/** lasciare così */}
              <Card
                sx={{
                  p: 3,
                  maxHeight: `${tableHeight}px`,
                }}
              >
                {renderToolbar()}
                <Box
                  sx={{ p: 2, height: `${tableHeight}px`, overflow: "auto"}}
                >
                  <DataGridPro
                    sx={{
                      "& .MuiDataGrid-root, & .MuiDataGrid-footerContainer": {
                        minHeight: "2rem",
                      },
                      "&  .MuiDataGrid-cell ": {
                        cursor: "default",
                      },
                      borderColor: darkMode ? "#6B635A" : "",
                      "& .MuiDataGrid-cell, &.MuiDataGrid-columnHeaders": {
                        borderColor: darkMode ? "#625a52" : "",
                      },
                      "& .MuiDataGrid-columnSeparator": {
                        borderColor: darkMode ? "#625a52" : "",
                      },
                      "--DataGrid-rowBorderColor": darkMode ? "#625a52" : "",
                      minHeight: "20vh",
                      flexFlow: 1,
                      height: "100%",
                    }}
                    localeText={
                      language === "it"
                        ? itIT.components.MuiDataGrid.defaultProps.localeText
                        : language === "ge"
                        ? deDE.components.MuiDataGrid.defaultProps.localeText
                        : ""
                    }
                    rows={filteredRows}
                    columns={columns}
                    checkboxSelection
                    getRowId={(row) => row.id}
                    initialState={{
                      density: "compact",
                      pinnedColumns: { right: ["action"] },
                    }}
                    rowModesModel={rowModesModel}
                    resizeThrottleMs={resizeThrottle}
                    loading={!state.fetched || resizeThrottle > 60}
                    onRowModesModelChange={(newModel) =>
                      setRowModesModel(newModel)
                    }
                    onRowSelectionModelChange={(newSelection) =>
                      handleRowSelection(newSelection)
                    }
                    apiRef={apiRef}
                    onColumnVisibilityModelChange={handleColumnVisibilityChange}
                    onSelectionModelChange={(newSelection) => {
                      const selectedRows = newSelection.map((id) =>
                        filteredRows.find((row) => row.id === id)
                      );
                    }}
                    pagination
                    autoPageSize
                    slotProps={{
                      loadingOverlay: {
                        variant: "skeleton",
                        noRowsVariant: "skeleton",
                      },
                    }}
                    slots={{
                      toolbar: () => (
                        <GridToolbarContainer>
                          <GridToolbarColumnsButton />
                          <GridToolbarExport
                            printOptions={{ disableToolbarButton: true }}
                            csvOptions={{
                              fileName: "Unencoded Alarms",
                              utf8WithBom: true,
                            }}
                          />
                          <GridToolbarFilterButton />
                        </GridToolbarContainer>
                      ),
                    }}
                  />
                </Box>
              </Card>
            </Box>
          </Grid>
        </Grid>
      </DashboardLayout>
      <EncodeDialog
        open={openEncodeDialog}
        onClose={() => {
          setOpenEncodeDialog(false);
          tlmGetAlarmDescription();
        }}
        language={language}
        selectedRows={selectedRows}
      />
      <APIStatusDialog
        open={apiStatus.open}
        status={apiStatus.status}
        message={apiStatus.message}
      />
    </>
  );
}

export default Billing;
